import { z } from 'zod';
import { listInput } from './list';
import { idempotency } from './spine';
import {
  CUSTOMER_PROBLEM_KINDS,
  TECH_FLAG_DEFAULT_KIND,
  TECH_FLAG_KINDS,
  TICKET_CATEGORIES,
  TICKET_MESSAGE_VISIBILITIES,
  TICKET_PRIORITIES,
  TICKET_ROOT_CAUSES,
  TICKET_SOURCES,
  TICKET_STATES,
} from '../constants/tickets';

/** A message as the office or a tech writes it: the words, and who may be told them. */
export const ticketMessageFields = z.object({
  body: z.string().trim().min(1).max(4000),
  visibility: z.enum(TICKET_MESSAGE_VISIBILITIES).default('internal'),
});

/**
 * The office's "New ticket": a customer always, a job when there is one (G-T3). Naming an owner
 * opens it straight away (spec §2: assigning a `new` ticket opens it). The first message is
 * optional; a phone complaint is often just its title.
 */
export const ticketCreateInput = idempotency.extend({
  customerId: z.string().uuid(),
  jobId: z.string().uuid().optional(),
  title: z.string().trim().min(1).max(200),
  category: z.enum(TICKET_CATEGORIES),
  priority: z.enum(TICKET_PRIORITIES).default('normal'),
  ownerId: z.string().uuid().optional(),
  replyDueAt: z.coerce.date().optional(),
  message: ticketMessageFields.optional(),
});
export type TicketCreateInput = z.infer<typeof ticketCreateInput>;

/** `mine` and `nobody` are the two owners every office asks about by name. */
export const ticketOwnerFilter = z.union([
  z.literal('mine'),
  z.literal('nobody'),
  z.string().uuid(),
]);
export type TicketOwnerFilter = z.infer<typeof ticketOwnerFilter>;

/**
 * The Tickets list's filters, all server-side (the list is paged, so a filter applied in the
 * browser would only filter the page it holds). Each list filter takes one value or several.
 * `overdue` is a reply-due time that has passed on a ticket that is not resolved.
 */
export const ticketListInput = listInput.extend({
  status: z.array(z.enum(TICKET_STATES)).min(1).max(TICKET_STATES.length).optional(),
  priority: z
    .array(z.enum(TICKET_PRIORITIES))
    .min(1)
    .max(TICKET_PRIORITIES.length)
    .optional(),
  category: z
    .array(z.enum(TICKET_CATEGORIES))
    .min(1)
    .max(TICKET_CATEGORIES.length)
    .optional(),
  /** Who raised it: the office, a tech's flag, or the customer from a link (Phase 5b). */
  source: z.array(z.enum(TICKET_SOURCES)).min(1).max(TICKET_SOURCES.length).optional(),
  owner: ticketOwnerFilter.optional(),
  overdue: z.boolean().optional(),
});
export type TicketListInput = z.infer<typeof ticketListInput>;

/** How many cards one Board column draws; the rest are "N more" in the List (spec §5). */
export const TICKET_BOARD_CAP = 100;

/** The Board's Resolved column holds what was resolved in the last this-many days. */
export const TICKET_BOARD_RESOLVED_DAYS = 14;

/**
 * The Board's filters: the List's, less the status (a column IS a status) and the paging (a
 * column is capped at `TICKET_BOARD_CAP` instead).
 */
export const ticketBoardInput = ticketListInput
  .pick({
    search: true,
    priority: true,
    category: true,
    source: true,
    owner: true,
    overdue: true,
  })
  .strict();
export type TicketBoardInput = z.infer<typeof ticketBoardInput>;

export const ticketIdInput = z.object({ ticketId: z.string().uuid() });
export type TicketIdInput = z.infer<typeof ticketIdInput>;

export const ticketForJobInput = z.object({ jobId: z.string().uuid() });
export type TicketForJobInput = z.infer<typeof ticketForJobInput>;

// ── Working a ticket (ticket 02) ─────────────────────────────────────────────
// Every write names its ticket and carries the idempotency envelope: a tech's message is
// replayed from the offline queue (ticket 03), and a retry must land once.
const onTicket = idempotency.extend({ ticketId: z.string().uuid() });

/**
 * The panel's inline facts. A key left out is left alone; `replyDueAt: null` clears the
 * promise. At least one field, or there is nothing to write.
 */
export const ticketUpdateInput = onTicket
  .extend({
    title: z.string().trim().min(1).max(200).optional(),
    category: z.enum(TICKET_CATEGORIES).optional(),
    priority: z.enum(TICKET_PRIORITIES).optional(),
    replyDueAt: z.coerce.date().nullable().optional(),
  })
  .refine(
    (v) =>
      v.title !== undefined ||
      v.category !== undefined ||
      v.priority !== undefined ||
      v.replyDueAt !== undefined,
    { message: 'Change at least one thing' },
  );
export type TicketUpdateInput = z.infer<typeof ticketUpdateInput>;

/** Name who owns it. Assigning a `new` ticket opens it (the machine's `ticket.assign`). */
export const ticketAssignInput = onTicket.extend({ ownerId: z.string().uuid() });
export type TicketAssignInput = z.infer<typeof ticketAssignInput>;

/**
 * The moves `ticket.transition` makes: into and out of the two waits, and reopening a resolved
 * ticket. Resolving has its own write (it needs what fixed it), and so does leaving `new`.
 */
export const TICKET_TRANSITION_TARGETS = [
  'open',
  'waiting_customer',
  'waiting_parts',
] as const satisfies readonly (typeof TICKET_STATES)[number][];
export const ticketTransitionInput = onTicket.extend({
  to: z.enum(TICKET_TRANSITION_TARGETS),
});
export type TicketTransitionInput = z.infer<typeof ticketTransitionInput>;

/** Resolving asks what fixed it and why it went wrong (spec §2); both are required. */
export const ticketResolveInput = onTicket.extend({
  resolution: z.string().trim().min(1).max(2000),
  rootCause: z.enum(TICKET_ROOT_CAUSES),
});
export type TicketResolveInput = z.infer<typeof ticketResolveInput>;

export const ticketMessageInput = onTicket.merge(ticketMessageFields);
export type TicketMessageInput = z.infer<typeof ticketMessageInput>;

/** Follow or unfollow: always the caller themselves. */
export const ticketFollowInput = onTicket;
export type TicketFollowInput = z.infer<typeof ticketFollowInput>;

// ── A tech flags a problem from the visit (ticket 03) ────────────────────────

/**
 * "Flag a problem" on the tech's visit. The ticket id is minted on the PHONE, so a flag made
 * with no signal has an id its photos can wait under (they queue against it, and send once the
 * flag has landed), and a replay of the same flag names the same ticket. The job and the
 * customer are NOT taken from the phone: the server reads them off the visit, which the tech
 * must be on. "What's wrong" becomes the ticket's first message (internal).
 *
 * `category` is the kind the tech chose (`TECH_FLAG_KINDS`, Phase 6a): the ticket is filed
 * under it. Left out, it is `other`: a flag queued offline before the kind existed replays
 * without it. Any other value is refused, never filed as something else.
 */
export const ticketFlagInput = idempotency.extend({
  ticketId: z.string().uuid(),
  visitId: z.string().uuid(),
  title: z.string().trim().min(1).max(200),
  message: z.string().trim().min(1).max(4000).optional(),
  category: z.enum(TECH_FLAG_KINDS).default(TECH_FLAG_DEFAULT_KIND),
});
export type TicketFlagInput = z.infer<typeof ticketFlagInput>;

// ── The customer's "Something not right?" form (Phase 5b, ticket 02) ─────────

/**
 * What the customer's form sends (spec §3), as the server reads it: no session, no delivery
 * key. The link it came through is not in here: the token in the URL decides that.
 *
 *  - `kind`: one of the four plain choices (`CUSTOMER_PROBLEM_KINDS`).
 *  - `description`: "What's not right?", 10–4000 characters once trimmed.
 *  - `contact`: "Best way to reach you", optional, up to 200; blank means none.
 *  - `nonce`: minted per render of the form; the double-submit guard, not a secret.
 *  - `website`: the honeypot. A person never sees it, so anything in it is a bot.
 */
export const customerProblemInput = z.object({
  kind: z.enum(CUSTOMER_PROBLEM_KINDS),
  description: z.string().trim().min(10).max(4000),
  contact: z
    .string()
    .trim()
    .max(200)
    .optional()
    .transform((v) => (v ? v : undefined)),
  nonce: z.string().uuid(),
  website: z.string().optional(),
});
export type CustomerProblemInput = z.input<typeof customerProblemInput>;
